import React, { FC } from 'react'
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-premium'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { SensorListFilters, SensorsMapView } from 'src/components'
import { SensorColumnsDataGrid } from 'src/components/columns/sensor'
import { Sensor, SensorState } from 'src/api'
import { useQueryParamsState } from 'src/hooks/ui'
import { SearchParamsKeys, ViewTypes, SensorFilters, SensorButtons } from 'src/enums'
import { SensorMapFilters } from 'src/components/forms/sensor/sensorMapFilters'
import { CommonSensorList } from './commonSensorsList'
import { SensorContextMenu, SensorMenuItemKey, useSensorContextMenu } from './sensorContextMenu'
import { ContractorsDataLoader } from './contractorsDataLoader'

interface InUseSensorListProps {
  isLoading: boolean
  count: number
  data: Sensor[]
  states: SensorState[]
  refetchSensorList: () => void
}

export const InUseSensorList: FC<InUseSensorListProps> = ({ data, isLoading, count, states, refetchSensorList }) => {
  const queryParams = useQueryParamsState()
  const viewType = queryParams[SearchParamsKeys.ViewType] || ViewTypes.List
  const { currentRow, menuAnchorEl, handleOpenMenu, handleCloseMenu } = useSensorContextMenu()

  const columns: GridColDef<Sensor>[] = [
    SensorColumnsDataGrid.Id(),
    SensorColumnsDataGrid.Model,
    SensorColumnsDataGrid.IMEI,
    SensorColumnsDataGrid.DevEUI,
    SensorColumnsDataGrid.Label,
    SensorColumnsDataGrid.LastActiveContractor,
    SensorColumnsDataGrid.LastPing,
    SensorColumnsDataGrid.BatteryLevel,
    SensorColumnsDataGrid.DateConnected,
    SensorColumnsDataGrid.TimeOnProject,
    SensorColumnsDataGrid.UsedAt,
    SensorColumnsDataGrid.City,
    SensorColumnsDataGrid.Project,
    SensorColumnsDataGrid.StateUpdatedAt,
    SensorColumnsDataGrid.StateAuthor,
    SensorColumnsDataGrid.OrderDate,
    SensorColumnsDataGrid.OrderNumber,
    SensorColumnsDataGrid.OrderPart,
    {
      field: 'actions',
      headerName: '',
      type: 'actions',
      width: 30,
      getActions: ({ row }) => [
        <GridActionsCellItem icon={<MoreVertIcon />} label="Actions" className="textPrimary" onClick={(e: any) => handleOpenMenu(e, row)} color="inherit" />
      ]
    }
  ]

  return viewType === ViewTypes.List ? (
    <>
      <CommonSensorList
        columns={columns}
        count={count}
        data={data}
        isLoading={isLoading}
        filters={
          <ContractorsDataLoader>
            <SensorListFilters
              activeFilters={[
                SensorFilters.LowBattery,
                SensorFilters.WeakSignal,
                SensorFilters.LastPingMoreThanHour,
                SensorFilters.SyncPeriod,
                SensorFilters.ReadingsAge,
                SensorFilters.CurrentSensorConfiguration,
                SensorFilters.BleStatus,
                SensorFilters.Contractor,
                SensorFilters.OrderNumber,
                SensorFilters.OrderDate
              ]}
            />
          </ContractorsDataLoader>
        }
        buttons={[SensorButtons.QualityControl, SensorButtons.GetHistoricalData, SensorButtons.ManageSensorBleState]}
        checkboxSelection
      />
      <SensorContextMenu
        menuItems={[SensorMenuItemKey.MarkAsLost]}
        currentRow={currentRow}
        menuAnchorEl={menuAnchorEl}
        onOpenMenu={handleOpenMenu}
        onCloseMenu={handleCloseMenu}
        states={states}
        refetchSensorList={refetchSensorList}
      />
    </>
  ) : (
    <SensorsMapView
      data={data}
      isLoading={isLoading}
      filters={
        <ContractorsDataLoader>
          <SensorMapFilters
            activeFilters={[
              SensorFilters.Contractor,
              SensorFilters.LowBattery,
              SensorFilters.WeakSignal,
              SensorFilters.LastPingMoreThanHour,
              SensorFilters.SyncPeriod,
              SensorFilters.ReadingsAge,
              SensorFilters.LocationType,
              SensorFilters.OrderNumber,
              SensorFilters.OrderDate
            ]}
          />
        </ContractorsDataLoader>
      }
    />
  )
}
